import React, { useState, useEffect } from 'react';
import { Typography, Button, Table, message, Input, Modal, Progress, Space, Tooltip, Card, Row, Col, Select, Tag } from 'antd';
import { ExportOutlined, DownloadOutlined, FilterOutlined } from '@ant-design/icons';
import { getTemplate } from '../utils/printerUtils';
import html2canvas from 'html2canvas';
import { fetchBatches, exportBatch, fetchBatchOrders, fetchBatchStatistics } from '../services/batchService';
import OrderList from './orders/OrderList';
import OrderDetailModal from './orders/OrderDetailModal';
import './MyBatches.css';

const { Text, Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const DEFAULT_ORDER_PAGE_SIZE = 500;

const BatchManagement = () => {
  const [loading, setLoading] = useState(true);
  const [allBatches, setAllBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderPagination, setOrderPagination] = useState({
    current: 1,
    pageSize: DEFAULT_ORDER_PAGE_SIZE,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [inspectionCount, setInspectionCount] = useState(0);
  const [showInspectionOnly, setShowInspectionOnly] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [batchToDownload, setBatchToDownload] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchBatchData = async () => {
    try {
      setLoading(true);
      const [batchResponse, statsResponse] = await Promise.all([
        fetchBatches(),
        fetchBatchStatistics()
      ]);
      setAllBatches(batchResponse.results);
      setFilteredBatches(batchResponse.results);
      setStatistics(statsResponse);
    } catch (error) {
      console.error('Error fetching batches:', error);
      message.error('加载批次数据失败');
    } finally {
      setLoading(false);
    }
  };

  const loadBatchOrders = async (batchId, page = 1) => {
    if (!batchId) return;
    
    try {
      setOrderLoading(true);
      const response = await fetchBatchOrders(batchId, page, DEFAULT_ORDER_PAGE_SIZE);
      
      // Handle different response structures
      const orders = response?.results || response?.data || response || [];
      const total = response?.count || response?.total || orders.length || 0;
      
      setOrders(orders);
      setOrderPagination({
        ...orderPagination,
        current: page,
        total: total
      });
      
      // Count inspection orders - ensure orders is an array
      const inspectionOrders = Array.isArray(orders) ? orders.filter(order => 
        order.status === 'INSPECTION_NEEDED' || order.status === 'INSPECTED'
      ) : [];
      setInspectionCount(inspectionOrders.length);
    } catch (error) {
      console.error('Error fetching batch orders:', error);
      message.error('加载订单失败');
      setOrders([]);
      setInspectionCount(0);
    } finally {
      setOrderLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchData();
  }, []);

  useEffect(() => {
    if (selectedBatch) {
      loadBatchOrders(selectedBatch.id, 1);
      setIsSearchMode(false);
      setShowInspectionOnly(false);
    } else {
      setOrders([]);
      setInspectionCount(0);
    }
  }, [selectedBatch]);

  const handleSearch = (value) => {
    setSearchText(value);
    applyFilters(value, statusFilter);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    applyFilters(searchText, status);
  };

  const applyFilters = (search, status) => {
    let filtered = allBatches;

    // Apply search filter
    if (search) {
      filtered = filtered.filter(batch => 
        batch.batch_number.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Apply status filter
    if (status !== 'all' && statistics) {
      const statusBatches = statistics.batches_by_status.filter(batch => {
        switch (status) {
          case 'picked_up':
            return batch.airway_bill_status === 'PICKED_UP';
          case 'inspected_pending':
            return batch.airway_bill_status === 'INSPECTED_PENDING';
          case 'released':
            return batch.airway_bill_status === 'RELEASED';
          default:
            return true;
        }
      });
      const statusBatchIds = statusBatches.map(b => b.id);
      filtered = filtered.filter(batch => statusBatchIds.includes(batch.id));
    }

    setFilteredBatches(filtered);
  };

  const handleOrderPageChange = (page) => {
    if (selectedBatch) {
      loadBatchOrders(selectedBatch.id, page);
    }
  };

  const generateLabel = async (order) => {
    try {
      const tempDiv = document.createElement('div');
      tempDiv.style.width = '100mm';
      tempDiv.style.height = '150mm';
      tempDiv.style.padding = '0';
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.boxSizing = 'border-box';

      let template;
      try {
        template = getTemplate(order.courier_service, order.port);
      } catch (error) {
        console.error('Template selection failed:', error);
        message.error(error.message);
        return null;
      }

      const orderWithCorrectAddress = {
        ...order,
        recipient_name: order.label_address ? order.label_recipient : order.recipient_name,
        recipient_phone: order.label_address ? order.label_phone : order.recipient_phone,
        recipient_address: order.label_address ? order.label_address : order.recipient_address
      };
      const labelHtml = await template(orderWithCorrectAddress);
      if (!labelHtml) {
        message.error('面单生成失败');
        return null;
      }

      tempDiv.innerHTML = labelHtml;
      document.body.appendChild(tempDiv);

      await new Promise(resolve => setTimeout(resolve, 300));

      const canvas = await html2canvas(tempDiv, {
        scale: 4,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 378,
        height: 567,
        onclone: (clonedDoc) => {
          const clonedDiv = clonedDoc.querySelector('div');
          if (clonedDiv) {
            clonedDiv.style.width = '378px';
            clonedDiv.style.height = '567px';
          }
        }
      });

      document.body.removeChild(tempDiv);
      return canvas;
    } catch (error) {
      console.error('Label generation error:', error);
      message.error('面单生成失败');
      return null;
    }
  };

  const handleDownloadLabels = async (batch, e) => {
    e.stopPropagation();
    setBatchToDownload(batch);
    setDownloadModalVisible(true);
  };

  const handleConfirmDownload = async () => {
    setIsGenerating(true);
    setDownloadProgress(0);

    try {
      // Get all orders for the batch
      const response = await fetchBatchOrders(batchToDownload.id, 1, batchToDownload.order_count);
      
      // Handle different response structures
      const orders = response?.results || response?.data || response || [];
      const totalOrders = orders.length;
      const labelCanvases = [];

      if (!Array.isArray(orders) || orders.length === 0) {
        throw new Error('No orders found for this batch');
      }

      // Generate labels with progress updates
      for (let i = 0; i < orders.length; i++) {
        const canvas = await generateLabel(orders[i]);
        if (canvas) {
          labelCanvases.push({
            canvas,
            orderNumber: orders[i].order_number
          });
        }
        setDownloadProgress(Math.round(((i + 1) / totalOrders) * 100));
      }

      if (labelCanvases.length === 0) {
        throw new Error('No labels were generated successfully');
      }

      // Convert canvases to PDFs and create zip
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Add each label as a JPEG to the zip
      labelCanvases.forEach(({ canvas, orderNumber }) => {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
        const base64Data = dataUrl.split(',')[1];
        zip.file(`${orderNumber}.jpg`, base64Data, { base64: true });
      });

      // Generate and download zip
      const content = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `批次${batchToDownload.batch_number}_面单.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('面单下载成功');
    } catch (error) {
      console.error('Download error:', error);
      message.error('面单下载失败');
    } finally {
      setIsGenerating(false);
      setDownloadModalVisible(false);
      setDownloadProgress(0);
      setBatchToDownload(null);
    }
  };

  const handleExportBatch = async (batch, e) => {
    e.stopPropagation();
    try {
      await exportBatch(batch);
      message.success('批次导出成功');
    } catch (error) {
      console.error('Error exporting batch:', error);
      message.error('导出批次失败');
    }
  };

  const handleSearchResults = (results) => {
    setOrders(results);
    setIsSearchMode(true);
  };

  const handleClearSearch = () => {
    setIsSearchMode(false);
    setShowInspectionOnly(false);
    if (selectedBatch) {
      loadBatchOrders(selectedBatch.id, 1);
    }
  };

  const toggleInspectionFilter = () => {
    setShowInspectionOnly(!showInspectionOnly);
    if (!showInspectionOnly) {
      const inspectionOrders = orders.filter(order => 
        order.status === 'INSPECTION_NEEDED' || order.status === 'INSPECTED'
      );
      handleSearchResults(inspectionOrders);
    } else {
      handleClearSearch();
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setModalVisible(true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PICKED_UP':
        return 'orange';
      case 'INSPECTED_PENDING':
        return 'blue';
      case 'RELEASED':
        return 'green';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: '批次号',
      dataIndex: 'batch_number',
      key: 'batch_number',
      ellipsis: true,
      width: 80,
    },
    {
      title: '数量',
      dataIndex: 'order_count',
      key: 'order_count',
      width: 50,
    },
    {
      title: '状态',
      key: 'status',
      width: 80,
      render: (_, record) => {
        const batchInfo = statistics?.batches_by_status?.find(b => b.id === record.id);
        if (batchInfo && batchInfo.airway_bill_status) {
          return (
            <Tag color={getStatusColor(batchInfo.airway_bill_status)} size="small">
              {batchInfo.status_display}
            </Tag>
          );
        }
        return <Tag size="small">未分配</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 70,
      render: (text) => formatDate(text),
    },
    {
      title: '',
      key: 'action',
      width: 60,
      align: 'left',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="导出批次">
            <Button
              type="text"
              size="small"
              icon={<ExportOutlined />}
              onClick={(e) => handleExportBatch(record, e)}
              style={{ color: '#666', padding: '0 4px', height: '16px', fontSize: '11px' }}
            />
          </Tooltip>
          <Tooltip title="下载面单">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={(e) => handleDownloadLabels(record, e)}
              style={{ color: '#666', padding: '0 4px', height: '16px', fontSize: '11px' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ padding: '16px' }}>
        {/* Statistics Dashboard */}
        {statistics && (
          <div style={{ marginBottom: '16px' }}>
            <Title level={4} style={{ marginBottom: '16px' }}>批次管理</Title>
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={6}>
                <Card 
                  size="small" 
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                  onClick={() => handleStatusFilter('all')}
                  className={statusFilter === 'all' ? 'selected-card' : ''}
                >
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                    {statistics.total_batches}
                  </div>
                  <div style={{ color: '#666' }}>合计批次</div>
                </Card>
              </Col>
              <Col span={6}>
                <Card 
                  size="small" 
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                  onClick={() => handleStatusFilter('picked_up')}
                  className={statusFilter === 'picked_up' ? 'selected-card' : ''}
                >
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16' }}>
                    {statistics.picked_up_not_processed}
                  </div>
                  <div style={{ color: '#666' }}>已提货未过机</div>
                </Card>
              </Col>
              <Col span={6}>
                <Card 
                  size="small" 
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                  onClick={() => handleStatusFilter('inspected_pending')}
                  className={statusFilter === 'inspected_pending' ? 'selected-card' : ''}
                >
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                    {statistics.inspected_not_released}
                  </div>
                  <div style={{ color: '#666' }}>已出查验未放行</div>
                </Card>
              </Col>
              <Col span={6}>
                <Card 
                  size="small" 
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                  onClick={() => handleStatusFilter('released')}
                  className={statusFilter === 'released' ? 'selected-card' : ''}
                >
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                    {statistics.released}
                  </div>
                  <div style={{ color: '#666' }}>已放行</div>
                </Card>
              </Col>
            </Row>
          </div>
        )}

        {/* Filters */}
        <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Search
            placeholder="搜索批次号"
            allowClear
            value={searchText}
            onChange={e => handleSearch(e.target.value)}
            style={{ width: '300px' }}
            size="middle"
          />
          <Select
            value={statusFilter}
            onChange={handleStatusFilter}
            style={{ width: '200px' }}
            size="middle"
          >
            <Option value="all">全部状态</Option>
            <Option value="picked_up">已提货未过机</Option>
            <Option value="inspected_pending">已出查验未放行</Option>
            <Option value="released">已放行</Option>
          </Select>
          {statusFilter !== 'all' && (
            <Button onClick={() => handleStatusFilter('all')} size="middle">
              清除筛选
            </Button>
          )}
        </div>

        {/* Batch List and Orders */}
        {selectedBatch ? (
          <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 300px)' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              padding: '12px 16px',
              backgroundColor: '#fafafa',
              borderRadius: '6px',
              marginBottom: '16px'
            }}>
              <Button 
                type="link" 
                onClick={() => setSelectedBatch(null)}
                style={{ padding: 0, height: 'auto' }}
              >
                ← 返回批次列表
              </Button>
              <Text strong>批次 {selectedBatch.batch_number}</Text>
              <Text>订单总数：{orderPagination.total}</Text>
              <Button
                type={showInspectionOnly ? "primary" : "default"}
                size="small"
                onClick={toggleInspectionFilter}
              >
                查验件：{inspectionCount}
              </Button>
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <OrderList
                orders={orders}
                loading={orderLoading}
                pagination={orderPagination}
                onPageChange={handleOrderPageChange}
                showBatchButtons={false}
                isSearchMode={isSearchMode}
                onSearchResults={handleSearchResults}
                onClearSearch={handleClearSearch}
                onOrdersUpdate={() => {
                  if (selectedBatch) {
                    loadBatchOrders(selectedBatch.id, 1);
                  }
                }}
                onOrderClick={handleOrderClick}
              />
            </div>
          </div>
        ) : (
          <div style={{ height: 'calc(100vh - 300px)' }}>
            <Card title="批次列表" size="small" style={{ height: '100%' }}>
              <Table
                columns={columns}
                dataSource={filteredBatches}
                rowKey="id"
                pagination={{
                  pageSize: 20,
                  showSizeChanger: false,
                  showQuickJumper: true,
                  showTotal: (total) => `共 ${total} 个批次`
                }}
                loading={loading}
                size="small"
                onRow={(record) => ({
                  onClick: () => setSelectedBatch(record),
                  style: { 
                    cursor: 'pointer'
                  }
                })}
                scroll={{ y: 'calc(100vh - 450px)' }}
              />
            </Card>
          </div>
        )}
      </div>

      <OrderDetailModal
        visible={modalVisible}
        order={selectedOrder}
        onClose={() => setModalVisible(false)}
      />

      <Modal
        title="下载面单"
        open={downloadModalVisible}
        onOk={handleConfirmDownload}
        onCancel={() => {
          setDownloadModalVisible(false);
          setBatchToDownload(null);
        }}
        okText="确认"
        cancelText="取消"
        okButtonProps={{ disabled: isGenerating }}
        cancelButtonProps={{ disabled: isGenerating }}
      >
        {isGenerating ? (
          <div>
            <p>正在生成面单，请稍候...</p>
            <Progress percent={downloadProgress} status="active" />
          </div>
        ) : (
          <p>确定下载此批次的所有面单PDF文件吗？</p>
        )}
      </Modal>

      <style jsx>{`
        .selected-card {
          border-color: #1890ff;
          box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
        }
      `}</style>
    </>
  );
};

export default BatchManagement;
